import type { DashboardWidgetConfig } from './dashboardConfig';
import { expectedMetricOf, isExpectedField, isTrendWidget } from './dashboardConfig';
import { isCalcField } from './dashboardCalculations';

// The data preparation behind a custom dashboard widget's chart: turning report rows into
// ranked categories, the top N plus an "Others" bucket, and the two-dimension combo / pivot
// views. Pure, so it is tested without recharts (which renders nothing in the test DOM).

export const MAX_VISIBLE = 5;
export const OTHERS_LABEL = 'Others';
export const EXPECTED_KEY = '__expected';
export const ACTUAL_KEY = '__actual';
export const PIVOT_TOTAL_KEY = '__pivotTotal';
export const DIM_PREFIX = '__dim_';

export type ChartPoint = Record<string, number | string>;
// One row of a report response: `<field>_<aggregation>` metric columns and dimension columns
// (a dimension's display label, when it has one, comes as `<field>_`).
export type DashboardReportRow = Record<string, unknown>;
export type ComboPoint = { name: string; value: number };
export type PivotData = { points: ChartPoint[]; series: string[] };

export const metricKey = (m: { field: string; aggregation: string }) =>
    `${m.field}_${m.aggregation}`;

// What a widget's config means for the chart maths, resolved once per config.
export type WidgetChartMaths = {
    isTrend: boolean;
    bottomFirst: boolean;
    rankOrder: 1 | -1;
    showComparison: boolean;
    expectedKey: string | undefined;
    receivedKeys: string[];
    receivedOf: (point: ChartPoint) => number;
    expectedOf: (point: ChartPoint) => number;
    rowValue: (row: DashboardReportRow) => number;
};

export const widgetChartMaths = (config: DashboardWidgetConfig): WidgetChartMaths => {
    // A trend runs along its dimension (a date axis, typically) rather than ranking
    // categories, so it opts out of top N, the "Others" bucket and value-ordering.
    const isTrend = isTrendWidget(config);
    // Bottom N ranks lowest first, so the same "first N, rest in Others" cap takes the
    // bottom of the ranking. Alphabetical and trend order are left alone.
    const bottomFirst = config.rankFrom === 'bottom' && !isTrend;
    const rankOrder = bottomFirst ? -1 : 1;
    const expectedMetric = expectedMetricOf(config.metrics);
    const showComparison = config.comparison === true && expectedMetric != null;

    // A comparison reads its two figures from the widget's OWN metrics so it works for any
    // family (impacts, plays, time, …): "received" sums the metrics whose field mentions
    // "received"; "expected" is the expected (or Geopath audience) metric.
    const receivedKeys = config.metrics.filter(m => /received/i.test(m.field)).map(metricKey);
    const expectedKey = expectedMetric ? metricKey(expectedMetric) : undefined;

    const receivedOf = (point: ChartPoint): number => {
        // Without a "received" family, delivery is every metric that isn't the comparison.
        const keys = receivedKeys.length
            ? receivedKeys
            : config.metrics.filter(m => !isExpectedField(m.field)).map(metricKey);
        return keys.reduce((acc, k) => acc + Number(point[k] ?? 0), 0);
    };
    const expectedOf = (point: ChartPoint): number =>
        expectedKey ? Number(point[expectedKey] ?? 0) : 0;

    // The single value a 2-dimension graph plots per row: the delivered/received total when
    // comparison is on, else the first metric.
    const rowValue = (row: DashboardReportRow): number => {
        if (showComparison) {
            const p: ChartPoint = {};
            config.metrics.forEach(m => {
                p[metricKey(m)] = Number(row[metricKey(m)] ?? 0);
            });
            return receivedOf(p);
        }
        const primary = config.metrics[0] ? metricKey(config.metrics[0]) : undefined;
        return primary ? Number(row[primary] ?? 0) : 0;
    };

    return {
        isTrend,
        bottomFirst,
        rankOrder,
        showComparison,
        expectedKey,
        receivedKeys,
        receivedOf,
        expectedOf,
        rowValue,
    };
};

const dimLabel = (row: DashboardReportRow, field: string): string =>
    String(row[`${field}_`] ?? row[field] ?? '—');

// Full data with expected/actual per category, sorted by the chosen ranking basis.
export const buildChartData = (
    rows: DashboardReportRow[],
    config: DashboardWidgetConfig,
    maths: WidgetChartMaths,
): ChartPoint[] => {
    const { showComparison, isTrend, bottomFirst, rankOrder } = maths;
    const dimField = config.dimensions[0]?.field;
    const primary = config.metrics[0] ? metricKey(config.metrics[0]) : undefined;
    const mapped = rows.map((row, index) => {
        const name = dimField ? dimLabel(row, dimField) : `#${index + 1}`;
        // The figures a calculated measure is worked out from ride along, so an "Others" entry
        // can work it out again on their totals.
        const point: ChartPoint = Object.fromEntries(
            Object.entries(row).filter(([, value]) => typeof value === 'number'),
        ) as ChartPoint;
        point.name = name;
        // Keep every dimension value so a table can show a column per dimension.
        config.dimensions.forEach(d => {
            point[`${DIM_PREFIX}${d.field}`] = dimLabel(row, d.field);
        });
        config.metrics.forEach(m => {
            const value = row[metricKey(m)];
            // A calculated field with no value (a ratio against nothing) stays empty — NaN, so
            // it shows as a dash — rather than reading as a real zero.
            point[metricKey(m)] = value == null && isCalcField(m.field) ? NaN : Number(value ?? 0);
        });
        point[EXPECTED_KEY] = maths.expectedOf(point);
        point[ACTUAL_KEY] = maths.receivedOf(point);
        return point;
    });

    const displayKey = primary;
    const sortValue = (p: ChartPoint): number => {
        const expected = Number(p[EXPECTED_KEY] ?? 0);
        const actual = Number(p[ACTUAL_KEY] ?? 0);
        switch (config.sortBy) {
            case 'percentage':
                return expected > 0 ? (actual / expected) * 100 : 0;
            case 'difference':
                return actual - expected;
            case 'value':
                return showComparison ? actual : Number(p[displayKey ?? ''] ?? 0);
            default:
                // A comparison ranks on what was delivered (the total impressions), not on
                // whichever metric happens to be listed first.
                return showComparison ? actual : Number(p[displayKey ?? ''] ?? actual);
        }
    };

    if (config.sortBy === 'name' || (isTrend && !config.sortBy)) {
        return [...mapped].sort((a, b) =>
            String(a.name).localeCompare(String(b.name), undefined, { numeric: true }),
        );
    }
    if (!displayKey && !config.sortBy && !showComparison) {
        return bottomFirst ? [...mapped].reverse() : mapped;
    }
    // Empty values rank as zero, so the comparison never sees a NaN.
    const rankValue = (p: ChartPoint) => {
        const v = sortValue(p);
        return Number.isFinite(v) ? v : 0;
    };
    return [...mapped].sort((a, b) => rankOrder * (rankValue(b) - rankValue(a)));
};

// Top N is capped at MAX_VISIBLE (5) regardless of any stored value.
export const maxVisibleOf = (topN: number | null | undefined): number =>
    topN != null && topN > 0 ? Math.min(MAX_VISIBLE, Math.floor(topN)) : MAX_VISIBLE;

// Top N, plus an aggregated "Others" bucket when asked for: each metric summed, or, given
// `totalOf`, added up the way a totals row is, so a ratio is the ratio of the totals.
export const capChartData = (
    chartData: ChartPoint[],
    config: DashboardWidgetConfig,
    maths: WidgetChartMaths,
    maxVisible: number,
    showOthers: boolean,
    totalOf?: (points: ChartPoint[]) => Record<string, unknown>,
): ChartPoint[] => {
    if (maths.isTrend || chartData.length <= maxVisible) return chartData;
    const top = chartData.slice(0, maxVisible);
    if (!showOthers) return top;
    const rest = chartData.slice(maxVisible);
    const others: ChartPoint = { name: OTHERS_LABEL };
    // First dimension column shows "Others"; any further dimension columns stay blank.
    config.dimensions.forEach((d, i) => {
        others[`${DIM_PREFIX}${d.field}`] = i === 0 ? OTHERS_LABEL : '';
    });
    const total = totalOf?.(rest);
    config.metrics.forEach(m => {
        const k = metricKey(m);
        others[k] = total
            ? Number(total[k] ?? NaN)
            : rest.reduce((acc, row) => acc + Number(row[k] ?? 0), 0);
    });
    others[EXPECTED_KEY] = rest.reduce((acc, row) => acc + Number(row[EXPECTED_KEY] ?? 0), 0);
    others[ACTUAL_KEY] = rest.reduce((acc, row) => acc + Number(row[ACTUAL_KEY] ?? 0), 0);
    return [...top, others];
};

// A line with a second dimension splits each category (dim 1) into a series per distinct
// dim-2 value. Value per cell is the primary metric.
// Full (uncapped) list; the card view caps to top N + Others, the breakdown shows all.
export const buildPivot = (
    rows: DashboardReportRow[],
    config: DashboardWidgetConfig,
    maths: WidgetChartMaths,
): PivotData | null => {
    const [first, second] = config.dimensions;
    if (!first || !second) return null;
    const dim1 = first.field;
    const dim2 = second.field;
    const cats: ChartPoint[] = [];
    const byCat = new Map<string, ChartPoint>();
    const seriesTotals = new Map<string, number>();
    rows.forEach(row => {
        const cat = dimLabel(row, dim1);
        const ser = dimLabel(row, dim2);
        const v = maths.rowValue(row);
        let pt = byCat.get(cat);
        if (!pt) {
            pt = { name: cat, [PIVOT_TOTAL_KEY]: 0 };
            byCat.set(cat, pt);
            cats.push(pt);
        }
        pt[ser] = Number(pt[ser] ?? 0) + v;
        pt[PIVOT_TOTAL_KEY] = Number(pt[PIVOT_TOTAL_KEY]) + v;
        seriesTotals.set(ser, (seriesTotals.get(ser) ?? 0) + v);
    });
    if (config.sortBy === 'name') {
        cats.sort((a, b) =>
            String(a.name).localeCompare(String(b.name), undefined, { numeric: true }),
        );
    } else {
        cats.sort(
            (a, b) => maths.rankOrder * (Number(b[PIVOT_TOTAL_KEY]) - Number(a[PIVOT_TOTAL_KEY])),
        );
    }
    const series = [...seriesTotals.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
    return { points: cats, series };
};

// Top-N "dimension A - dimension B" combos for the bar graphs (axis and axis-less).
// Full (uncapped) list; the card view caps to top N + Others, the breakdown shows all.
export const buildComboData = (
    rows: DashboardReportRow[],
    config: DashboardWidgetConfig,
    maths: WidgetChartMaths,
): ComboPoint[] | null => {
    const [first, second] = config.dimensions;
    if (!first || !second) return null;
    const points = rows.map(row => ({
        name: `${dimLabel(row, first.field)} - ${dimLabel(row, second.field)}`,
        value: maths.rowValue(row),
    }));
    if (config.sortBy === 'name') {
        points.sort((x, y) => x.name.localeCompare(y.name, undefined, { numeric: true }));
    } else {
        points.sort((x, y) => maths.rankOrder * (y.value - x.value));
    }
    return points;
};

// Cap a combo list to top N with an aggregated "Others" bar (matches the single-
// dimension charts). Passing expanded=true keeps every combo (used by the breakdown).
export const capCombo = (
    pts: ComboPoint[] | null,
    expanded: boolean,
    maxVisible: number,
    showOthers: boolean,
): ComboPoint[] | null => {
    if (!pts || expanded || pts.length <= maxVisible) return pts;
    if (!showOthers) return pts.slice(0, maxVisible);
    const rest = pts.slice(maxVisible);
    const others = { name: OTHERS_LABEL, value: rest.reduce((acc, p) => acc + p.value, 0) };
    return [...pts.slice(0, maxVisible), others];
};

export const capPivot = (
    pv: PivotData | null,
    expanded: boolean,
    maxVisible: number,
    showOthers: boolean,
): PivotData | null => {
    if (!pv || expanded || pv.points.length <= maxVisible) return pv;
    if (!showOthers) return { points: pv.points.slice(0, maxVisible), series: pv.series };
    const rest = pv.points.slice(maxVisible);
    const others: ChartPoint = {
        name: OTHERS_LABEL,
        [PIVOT_TOTAL_KEY]: 0,
    };
    pv.series.forEach(s => {
        others[s] = rest.reduce((acc, p) => acc + Number(p[s] ?? 0), 0);
    });
    return { points: [...pv.points.slice(0, maxVisible), others], series: pv.series };
};

// The percentage label a pie/donut slice gets when "percent of total" is on: its share of
// what is plotted, to one decimal. Null when the slice should show its value instead.
export const slicePercentLabel = (
    value: number,
    total: number,
    percentOfTotal: boolean,
): string | null => (percentOfTotal && total > 0 ? `${((value / total) * 100).toFixed(1)}%` : null);
